import React, { useState, useCallback, useRef, useEffect } from "react";
import "./Tawksessions.scss";
import { Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AppTable } from "../../components/common";
import * as Cookie from "universal-cookie";

const cookie = new Cookie.default();
const cachedToken = cookie.get("userAuthTokenAdmin");

// ─── Types ────────────────────────────────────────────────────────────────────

interface TawkApiChat {
  id: string;
  type: string;
  pageId: string;
  propertyId: string;
  status: "open" | "closed";
  visitor: {
    name: string;   
    id: string;
    email: string | null;
  };
  location: {
    countryCode: string;
    city: string;
  };
  messageCount: number;
  chatDuration: number; 
  rating: number;      
  agents: string[];    
  createdOn: string;
  updatedOn: string;   
  domain: string;
  ip: string;
  personId: string;
  lastMessage: {
    text: string;
    time: string;
    sender: { t: string; id?: string; n?: string };
  };
    soulsara: {
    userId: string;
    amberStatus: boolean;
    redFlagStatus: boolean;
    sessionId: string | null;
    sessionNotes: {
      present: boolean;
      status: string | null;
    };
    sessionReport: {
      present: boolean;
      status: string | null;
    };
  };
}


interface TawkSession {
  personId: string;
  soulsaraUserId: string;
  totalMsgs: number;
  totalDurSecs: number;
  dur: string;
  counselor: string;
  sessionNotes: "pending" | "done";
  sessionReports: "pending" | "done" | "none";
  updatedOnIso: string;
  upd: string;
  st: "open" | "closed";
  am: any;
  rf: boolean;
  sessionCount: number;
  city: string;
  countryCode: string;
  lastMsg: string;
}

type DateFilter = "Today" | "Last 7 days" | "Last 30 days" | "All Time";
type NotesFilter = "all" | "pending";
type SortFilter = "desc" | "asc";


const BASE_URL = process.env.REACT_APP_API_URL ?? "";

const DATE_FILTER_DAYS: Record<DateFilter, number> = {
  Today: 1,
  "Last 7 days": 7,
  "Last 30 days": 30,
  "All Time": Infinity,
};

function isWithinDays(isoDate: string, days: number): boolean {
  if (!isFinite(days)) return true;  // "All Time" — always pass
  return Date.now() - new Date(isoDate).getTime() <= days * 86_400_000;
}

function fmtDuration(secs: number): string {
  if (secs === 0) return "< 1 sec";
  if (secs < 60) return `${secs} sec`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} min`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", { month: "short", day: "2-digit", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase()
  );
}


function groupChats(chats: TawkApiChat[], days: number): TawkSession[] {
  if (!Array.isArray(chats) || chats.length === 0) return [];
  const sessions: TawkSession[] = [];

  for (const chat of chats) {
 const ss = chat.soulsara;
  const uid = chat.soulsara.userId;

  if (!uid) continue;

  // show ONLY V-starting users
  if (uid.startsWith("V")) continue;

  if (!isWithinDays(chat.updatedOn, days)) continue;

  const hasAgents = chat.agents.length > 0;

  const counselor = chat.lastMessage.sender.n ?? (hasAgents ? "" : "");
    console.log(
  chats.map(c => ({
    user: c.visitor.name,
    amber: c.soulsara?.amberStatus
  })),"udhaya"
);

const noteStatus: TawkSession["sessionNotes"] =
  ss.sessionNotes.status === "done"
      ? "done"
      : "pending"
    

  const lastMsgText =
    chat.lastMessage.text === "CHAT_ENDED"
      ? "—"
      : chat.lastMessage.text;

  sessions.push({
  personId: chat.personId,
  soulsaraUserId: uid,
  totalMsgs: chat.messageCount,
  totalDurSecs: chat.chatDuration,
  dur: fmtDuration(chat.chatDuration),
  counselor,
  sessionNotes: noteStatus,
  sessionReports:
     ss.sessionReport.status === "done"
        ? "done"
        : "pending"
      ,
  updatedOnIso: chat.updatedOn,
  upd: fmtDate(chat.updatedOn),
  st: chat.status,
   am: ss?.amberStatus ? "Yes" : "No",
  rf: ss?.redFlagStatus ?? false,
  sessionCount: 1,
  city: chat.location.city,
  countryCode: chat.location.countryCode,
  lastMsg: lastMsgText,
});
}

  return sessions;
}



function NotesBadge({ val }: { val: "pending" | "done" | "none" }) {
  if (val === "pending") return <span className="badge_pending">Pending</span>;
  if (val === "done")    return <span className="badge_done">Done</span>;
  return <span className="badge_none">–</span>;
}


const TawkSessions: React.FC = () => {
  // ── Raw API data (unfiltered)
  const [allChats, setAllChats] = useState<TawkApiChat[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [totalApi, setTotalApi] = useState<number>(0);

  // ── Filters
  const [dateFilter,  setDateFilter]  = useState<DateFilter>("All Time");
  const [notesFilter, setNotesFilter] = useState<NotesFilter>("all");
  const [sortFilter,  setSortFilter]  = useState<SortFilter>("desc");
  const [amberOn,     setAmberOn]     = useState(false);
  const [redOn,       setRedOn]       = useState(false);
  const [query,       setQuery]       = useState("");
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(10);

  // ── Copy tooltip
  const [tipPos,     setTipPos]     = useState({ x: 0, y: 0 });
  const [tipVisible, setTipVisible] = useState(false);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchChats = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await fetch(`${BASE_URL}/tawk/chats?size=30`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cachedToken}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const json = await res.json();

      let chats: TawkApiChat[] = [];
      if (Array.isArray(json)) {
        chats = json;
      } else if (Array.isArray(json?.data?.chats)) {
        chats = json.data.chats;           // ← real shape
      } else if (Array.isArray(json?.data)) {
        chats = json.data;
      } else if (Array.isArray(json?.chats)) {
        chats = json.chats;
      } else {
        console.warn("[TawkSessions] Unexpected API response shape:", json);
      }

      setTotalApi(json?.data?.count ?? json?.total ?? chats.length);
      setAllChats(chats);
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChats(); }, [fetchChats]);

  const copyId = useCallback((id: string, e: React.MouseEvent) => {
    navigator.clipboard.writeText(id).then(() => {
      setTipPos({ x: e.clientX + 12, y: e.clientY - 8 });
      setTipVisible(true);
      if (tipTimer.current) clearTimeout(tipTimer.current);
      tipTimer.current = setTimeout(() => setTipVisible(false), 1400);
    });
  }, []);

  const grouped = groupChats(allChats, DATE_FILTER_DAYS[dateFilter]);

  // ── Client-side filter + sort
  // ── Client-side filter + sort
  const q = query.toLowerCase();
 let result = grouped.filter((r) =>
  (!amberOn || r.am === "Yes") &&
  (!redOn || r.rf === true) &&
  (notesFilter === "all" ||
    (notesFilter === "pending" &&
      (r.sessionNotes === "pending" ||
        r.sessionReports === "pending"))) &&
  (!q ||
    r.soulsaraUserId.toLowerCase().includes(q) ||
    r.personId.toLowerCase().includes(q) ||
    r.counselor.toLowerCase().includes(q))
);

  result = [...result].sort((a, b) =>
    sortFilter === "asc"
      ? new Date(a.updatedOnIso).getTime() - new Date(b.updatedOnIso).getTime()
      : new Date(b.updatedOnIso).getTime() - new Date(a.updatedOnIso).getTime()
  );

  const flaggedCount = result.filter((r) => r.rf).length;

  // Reset to page 1 on any filter change
  const setFilter = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  type Row = TawkSession & { key: string };
  const rows: Row[] = result.map((r, i) => ({ ...r, key: `${r.personId}-${r.soulsaraUserId}-${i}` }));

  const idCell = (id: string) => (
    <span className="cell_id" title={`Click to copy: ${id}`} onClick={(e) => copyId(id, e)}>
      {id}
    </span>
  );

  const columns: ColumnsType<Row> = [
    { title: "Contact / Client ID", dataIndex: "personId", key: "personId", render: (v: string) => idCell(v) },
    { title: "Channel User", dataIndex: "soulsaraUserId", key: "soulsaraUserId", render: (v: string) => idCell(v) },
    { title: "Total Msgs", dataIndex: "totalMsgs", key: "totalMsgs", render: (v: number) => <strong>{v}</strong> },
    { title: "Duration", dataIndex: "dur", key: "dur", render: (v: string) => <span className="cell_dur">{v}</span> },
    {
      title: "Counselor",
      dataIndex: "counselor",
      key: "counselor",
      render: (v: string) => (v ? <span className="counselor_name">{v}</span> : <span className="counselor_none">–</span>),
    },
    { title: "Session Notes", dataIndex: "sessionNotes", key: "sessionNotes", render: (v) => <NotesBadge val={v} /> },
    { title: "Session Reports", dataIndex: "sessionReports", key: "sessionReports", render: (v) => <NotesBadge val={v} /> },
    { title: "Last Updated", dataIndex: "upd", key: "upd", render: (v: string) => <span className="cell_date">{v}</span> },
    {
      title: "Status",
      dataIndex: "st",
      key: "st",
      render: (v: string) => (v === "open" ? <span className="badge_open">Open</span> : <span className="badge_closed">Closed</span>),
    },
    {
      title: "Amber",
      dataIndex: "am",
      key: "am",
      render: (v: string) => (v === "Yes" ? <span className="badge_red">Yes</span> : <span className="badge_none">No</span>),
    },
    {
      title: "Redflag",
      dataIndex: "rf",
      key: "rf",
      render: (v: boolean) => (v ? <span className="badge_red">Yes</span> : <span className="badge_none">No</span>),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <a href={`/user-files-session-history/tawk-chat/${r.soulsaraUserId}`} className="table_pill_btn table_pill_btn--view">
          View
        </a>
      ),
    },
  ];

  return (
    <div className="tawk_content">

      {/* Floating copy tooltip */}
      <div
        className={`copy_tip${tipVisible ? " show" : ""}`}
        style={{ left: tipPos.x, top: tipPos.y }}
      >
        Copied!
      </div>

      <div className="section_eyebrow">Counseling Management</div>
      <div className="page_title">Recent Tawk Sessions</div>
      <div className="page_sub">
        Showing Tawk.to chat activity · {dateFilter === "All Time" ? "all time" : dateFilter.toLowerCase()}
        {loading && <span className="loading_badge"> ⟳ Loading…</span>}
        {!loading && !apiError && (
          <span className="loaded_badge">
            {" · "}{result.length} unique users · {totalApi} raw chats from API
          </span>
        )}
      </div>

      {apiError && (
        <div className="api_error_banner">
          <span>⚠ Failed to load data: {apiError}</span>
          <button onClick={fetchChats}>Retry</button>
        </div>
      )}

      <div className="stats_row">
        <div className="stat_card">
          <div className="stat_num">{loading ? "—" : result.length}</div>
          <div className="stat_lbl">Total conversations in range</div>
        </div>
        <div className="stat_card is_pending">
          {/* <div className="stat_num amber">{loading ? "—" : 0}</div> */}
                    <div className="stat_num amber">{loading ? "—" : flaggedCount}</div>

          <div className="stat_lbl">Flagged in view</div>
        </div>
       
       
      </div>

      <div className="filter_card">

        <div className="filter_row">
          <div className="filter_group">
            <span className="filter_lbl">Date Range</span>
            <div className="pills">
              {(["Today", "Last 7 days", "Last 30 days", "All Time"] as DateFilter[]).map((label) => (
                <button
                  key={label}
                  className={`pill${dateFilter === label ? " active" : ""}`}
                  onClick={() => setFilter<DateFilter>(setDateFilter)(label)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        
        </div>

        <div className="filter_grid">

          <div className="filter_group">
            <span className="filter_lbl">Notes</span>
            <div className="pills">
              <button className={`pill${notesFilter === "all" ? " active" : ""}`} onClick={() => setFilter<NotesFilter>(setNotesFilter)("all")}>All</button>
              <button className={`pill${notesFilter === "pending" ? " active" : ""}`} onClick={() => setFilter<NotesFilter>(setNotesFilter)("pending")}>Pending</button>
            </div>
          </div>

          <div className="filter_group">
            <span className="filter_lbl">Sort by Date</span>
            <div className="pills">
              <button className={`pill${sortFilter === "desc" ? " active" : ""}`} onClick={() => setFilter<SortFilter>(setSortFilter)("desc")}>Newest first</button>
              <button className={`pill${sortFilter === "asc" ? " active" : ""}`} onClick={() => setFilter<SortFilter>(setSortFilter)("asc")}>Oldest first</button>
            </div>
          </div>

          <div className="filter_group">
            <span className="filter_lbl">Search</span>
            <Input.Search
              className="searchField"
              placeholder="Search here..."
              value={query}
              onChange={(e) => setFilter<string>(setQuery)(e.target.value)}
            />
          </div>

        </div>

        <div className="filter_row">
          <div className="filter_group">
            <span className="filter_lbl">Flags</span>
            <div className="tog_row">
              <div className="tog_item">
                <span className="tog_flag_icon">⚠️</span>
                <span className="tog_name amber">Amber</span>
                {/* <span className="tog_hint">(had counselor)</span> */}
                <label className="toggle_wrap">
                  <input type="checkbox" checked={amberOn} onChange={(e) => setFilter<boolean>(setAmberOn)(e.target.checked)} />
                  <span className="tog_track" />
                </label>
              </div>
              <div className="tog_item">
                <span className="tog_flag_icon">🚩</span>
                <span className="tog_name red">Redflag</span>
                {/* <span className="tog_hint">(multiple sessions)</span> */}
                <label className="toggle_wrap">
                  <input type="checkbox" checked={redOn} onChange={(e) => setFilter<boolean>(setRedOn)(e.target.checked)} />
                  <span className="tog_track_red" />
                </label>
              </div>
            </div>
          </div>
        </div>

      </div>

      <AppTable<Row>
        columns={columns}
        dataSource={rows}
        loading={loading}
        locale={{ emptyText: "No sessions match the current filters." }}
        pagination={{
          current: page,
          pageSize,
          onChange: (p, size) => {
            setPage(p);
            setPageSize(size);
          },
        }}
      />
    </div>
  );
};

export default TawkSessions;